import React, { useRef, useState } from 'react'
import { Drawer, List, ListItem, Avatar, IconButton, TextField, Button } from '@material-ui/core'
import { Edit } from '@material-ui/icons'
import { withStyles } from '@material-ui/core/styles'
import { useTranslation } from 'react-i18next'

import Subscribers from './subscribers/SubscribersContainer'
import MyEvents from './my-events/MyEventsContainer'
import PaidEvents from './paid-events/PaidEventsContainer'
import Gifts from './gifts/GiftsContainer'
import Shop from './shop/ShopContainer'
import AddCreditCard from './credit-cards/CreditCardsContainer'
import InterestingEvents from './interesting-events/InterestingEventsContainer'
import { resizeImageInBlob, resizeImageInBase64 } from '../../utils/imgUtil'

import defaultAvatar from '../../assets/images/Default.jpg'

const styles = theme => ({
  wrapper: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    paddingTop: 64
  },
  userInfo: { display: 'flex' },
  avatar: {
    margin: theme.spacing.unit,
    width: 200,
    height: 200
  },
  avatarPreview: {
    width: 200,
    height: 200,
    marginTop: 10
  },
  name: {
    marginLeft: '20%',
    fontSize: theme.typography.h1
  },
  editName: {
    marginLeft: '20%',
    marginTop: 20
  },
  contentWrapper: {
    width: '100%',
    position: 'relative',
    height: '100%',
    flex: '2 0 auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch'
  },
  drawerWrapper: {
    width: 200,
    height: 400,
    position: 'relative'
  },
  drawerProps: {
    position: 'absolute',
    width: 200
  },
  sectionContent: {
    position: 'relative',
    margin: theme.spacing.unit
  },
  editIcon: {
    top: 0,
    height: 48
  },
  changeAvatarWrapper: {
    display: 'flex',
    flexDirection: 'column',
    marginTop: 10
  },
  authButtons: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    marginBottom: 10
  }
})

/* The main component being shown in user's personal cabinet */
const Cabinet = ({ name, avatar, personId, classes, changeName, changeAvatar }) => {
  const { t } = useTranslation()
  // ref for the section selector container, required to place Drawer inside the div
  // https://github.com/mui-org/material-ui/issues/11749#issuecomment-452987953
  const drawerContainer = useRef(null)

  // list of cabinet sections
  const SECTIONS = {
    SUBSCRIBERS: 'subscribers',
    PAID_EVENTS: 'paid events',
    INTERESTING: 'interesting for me',
    GIFTS: 'gifts',
    SHOP: 'shop',
    MY_EVENTS: 'my events',
    ADD_CREDIT_CARD: 'add credit card',
    PORTFOLIO: 'my portfolio'
  }
  // state to store the currently selected section
  const [selectedSection, setSelectedSection] = useState(SECTIONS.ADD_CREDIT_CARD)
  const [isEditName, setEditingName] = useState(false)
  const [editName, setEditName] = useState(name)
  const [isChangeAvatar, setChangeAvatar] = useState(false)
  const [banner, setBanner] = useState({})

  const currentTab = () => {
    switch (selectedSection) {
      case SECTIONS.SUBSCRIBERS:
        return (<Subscribers />)

      case SECTIONS.PAID_EVENTS:
        return (<PaidEvents />)

      case SECTIONS.GIFTS:
        return (<Gifts />)

      case SECTIONS.SHOP:
        return (<Shop />)

      case SECTIONS.MY_EVENTS:
        return (<MyEvents />)

      case SECTIONS.ADD_CREDIT_CARD:
        return (<AddCreditCard />)

      case SECTIONS.INTERESTING:
        return (<InterestingEvents />)

      default: return t(`Cabinet:${selectedSection}`)
    }
  }

  const handleEditNameIcon = () => {
    if (isEditName) {
      setEditName(name)
      setEditingName(false)
    } else {
      setEditingName(true)
    }
  }

  const handleChangeAbatarIcon = () => {
    if (isChangeAvatar) {
      setBanner({})
      setChangeAvatar(false)
    } else {
      setChangeAvatar(true)
    }
  }

  const handleEditName = (event) => setEditName(event.target.value)

  const handleKeyPress = (ev) => {
    if (ev.key === 'Enter') {
      changeName(editName, personId)
      setEditingName(false)
      ev.preventDefault()
    }
  }

  const handleOnBlurEdit = (ev) => {
    changeName(editName, personId)
    setEditingName(false)
    ev.preventDefault()
  }

  const handleBannerInputChange = async (event) => {
    let file = event.target.files[0]
    const name = 'image-for-base64' + // Concat with file extension.
        file.name.substring(file.name.lastIndexOf('.'))
    // Instantiate copy of file, giving it new name.
    file = new File([file], name, { type: file.type })
    setBanner({
      blob: await resizeImageInBlob(event.target.files[0], 200, 200), // bytes array to be send with API call
      base64: await resizeImageInBase64(file, 200, 200) // to base 64 for save link in cooks
    })
  }

  const handleChangeBanner = () => {
    changeAvatar(banner, personId)
    setBanner({})
    setChangeAvatar(false)
  }

  return (
    <div className={classes.wrapper}>

      <div className={classes.userInfo}>
        {
          (!isChangeAvatar) ? (
            <Avatar src={(avatar || defaultAvatar)} className={classes.avatar} />
          ) : (
            <div className={classes.changeAvatarWrapper}>
              <input
                accept='image/*'
                id='contained-button-file'
                type='file'
                onChange={handleBannerInputChange}
              />

              { // show banner preview, only when image is chosen
                banner.base64
                  ? (
                    <div className={classes.authButtons}>
                      <Avatar src={banner.base64} className={classes.avatar} />
                      <Button onClick={handleChangeBanner} variant='contained' color='primary'>
                        {t('Cabinet:apply')}
                      </Button>
                    </div>
                  )
                  : undefined
              }
            </div>
          )
        }
        <IconButton
          aria-label={t('Cabinet:delete card')}
          size='small'
          className={classes.editIcon}
          onClick={handleChangeAbatarIcon}
        >
          <Edit/>
        </IconButton>
        {
          (isEditName) ? (
            <TextField
              value={editName}
              className={classes.editName}
              onChange={handleEditName}
              autoFocus
              onKeyPress={handleKeyPress}
              onBlur={handleOnBlurEdit}
            />
          ) : (
            <h1 className={classes.name}>
              {name}
            </h1>
          )
        }
        <IconButton
          aria-label={t('Cabinet:delete card')}
          size='small'
          className={classes.editIcon}
          onClick={() => handleEditNameIcon()}
        >
          <Edit/>
        </IconButton>
      </div>

      <div className={classes.contentWrapper}>

        {/* section selector */}
        <div>
          <div className={classes.drawerWrapper}
            ref={drawerContainer}>
            <Drawer
              variant='permanent'
              PaperProps={{ className: classes.drawerProps }} // required to keep Drawer in div
              BackdropProps={{ className: classes.drawerProps }} // the same
              ModalProps={{ // the same, as well
                container: drawerContainer.current,
                style: { position: 'absolute' }
              }}>
              <List>
                {
                  Object.values(SECTIONS).map((item) => (
                    <ListItem button
                      key={item}
                      selected={selectedSection === item}
                      onClick={() => setSelectedSection(item)}
                    >
                      {t(`Cabinet:${item}`)}
                    </ListItem>
                  ))
                }
              </List>
            </Drawer>
          </div>
        </div>

        {/* section content */}
        <div className={classes.sectionContent}>
          { currentTab() }
        </div>

      </div>

    </div>
  )
}

export default withStyles(styles)(Cabinet)
